import { Button, Card, Checkbox, Col, Modal, Row, Select } from 'antd';
import posthog from '@leyan/lytics';
import { useLocalStorageState } from 'ahooks';
import { downloadSingleAssistantPerf } from 'services/api/dashboard';
import FlexStyled from 'components/styled/flex';
import { momentDateRange2FetchParams } from 'views/Dashboard/ImplementData/utils';
import { FilterCtxType } from './Filter';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import { DatePicker } from 'components/DatePicker';
import { useCustomSelect } from './Content';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { currencyOptions } from 'views/Dashboard/ImplementData/components/Filter/CurrencySelector';
import dayjs, { Dayjs } from 'lib/dayjs';

interface Props {
  onClose: () => void;
  assistantIds: number[];
  initialValue: FilterCtxType;
}

const ExportAssistantModal = (props: Props) => {
  const { onClose, assistantIds, initialValue } = props;
  const { t } = useTranslate();
  const { currency } = useDashboardCurrency();

  const [filter, setFilter] = useState({ ...initialValue, currency: { ...currency } });

  const indicatorList = useMemo(
    () => [
      {
        label: t('export_pref_modal_reception_conversation_detail'),
        value: '接待会话明细',
      },
      {
        label: t('export_pref_modal_reception_bad_review_conversation_detail'),
        value: '接待后产生中差评会话明细',
      },
      {
        label: t('export_pref_modal_status_changed_record'),
        value: '状态变更记录',
      },
    ],
    [t],
  );
  const [checkedIndicators, setCheckedIndicators] = useLocalStorageState(
    `@fett/export-pref-assistant-indicator-sheet-v2`,
    {
      defaultValue: indicatorList.map((x) => x.value),
    },
  );

  const lang = useSystemLanguageValue();

  const params = useMemo(() => {
    const { dateRange, conversationType, departments, currency, ...rest } = filter;
    return {
      // 产品在提测以后加的需求，上线就被老板砍了
      conversationType: 2 as const,
      departmentIds: departments?.map((_) => _.id),
      currency: currency?.shorthand,
      ...rest,
      ...momentDateRange2FetchParams(dateRange),
    };
  }, [filter]);

  const { run, loading } = useExportFile();
  const onDownload = useCallback(() => {
    posthog.capture('$assistantPerformance', {
      type: 'downloadSingle',
    });

    run(() =>
      Promise.all(
        assistantIds.map((assistantId) =>
          downloadSingleAssistantPerf({
            ...params,
            assistantIds: [`${assistantId}`],
            language: lang,
            sheets: checkedIndicators ?? [],
          }),
        ),
      ).then(() => onClose?.()),
    );
  }, [run, params, assistantIds, lang, checkedIndicators, onClose]);

  const departmentSelect = (
    <DepartmentsSelect
      dashboardType="performance"
      value={filter.departments}
      onChange={(departments) => {
        setFilter((pre) => ({
          ...pre,
          departments,
          assistantIds: undefined,
        }));
      }}
    />
  );

  const { customSelect } = useCustomSelect({
    departments: filter.departments,
    value: filter.assistantIds,
    onChange: (value) => {
      setFilter((pre) => ({ ...pre, assistantIds: value }));
    },
  });

  return (
    <Modal
      onCancel={onClose}
      width={600}
      visible
      title={
        assistantIds.length > 1
          ? t('批量导出客服数据')
          : t('export_pref_modal_export_assistant_data')
      }
      footer={
        <FlexStyled.Flex className="justify-end">
          <span className="mr-4">
            {t('export_pref_modal_selected_x', { x: checkedIndicators?.length ?? 0 })}
          </span>
          <Button type="default" disabled={loading} onClick={onClose}>
            {t('translation.取消')}
          </Button>
          <Button
            type="primary"
            onClick={onDownload}
            loading={loading}
            disabled={!checkedIndicators?.length}
          >
            {t('export_pref_modal_button_export')}
          </Button>
        </FlexStyled.Flex>
      }
    >
      <Card
        className="!mb-4"
        title={
          <div className="flex gap-4">
            {departmentSelect}
            {(filter.departments || []).length > 0 && customSelect}
            <Select
              value={filter.currency?.region}
              options={currencyOptions}
              onChange={(value) => {
                const currency = currencyOptions.find((_) => _.region === value);
                setFilter((pre) => ({
                  ...pre,
                  currency: currency!,
                }));
              }}
            />
          </div>
        }
      >
        <span className="mr-4">{t('ViewDashboard.日期')}</span>
        <DatePicker.RangePicker
          className="w-[380px]"
          disabledDate={(current) => {
            return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
          }}
          value={filter.dateRange}
          onChange={(dateRange) => {
            setFilter((pre) => ({ ...pre, dateRange: dateRange as [Dayjs, Dayjs] }));
          }}
        />
      </Card>
      <Card
        title={
          <Checkbox
            className="flex "
            checked={checkedIndicators?.length === indicatorList.length}
            indeterminate={
              checkedIndicators &&
              checkedIndicators?.length > 0 &&
              checkedIndicators?.length < indicatorList.length
            }
            onChange={(e) =>
              setCheckedIndicators(e.target.checked ? indicatorList.map((x) => x.value) : [])
            }
          >
            {t('export_perf_modal_select_all')}
          </Checkbox>
        }
      >
        <Checkbox.Group
          onChange={(list) => setCheckedIndicators(list as string[])}
          value={checkedIndicators}
        >
          <Row gutter={[16, 32]}>
            {indicatorList.map((item) => (
              <Col span={12} key={item.value}>
                <Checkbox value={item.value}>{item.label}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Card>
    </Modal>
  );
};

export default ExportAssistantModal;
